import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { analyticsService } from '@common/analytics';
import { CUSTOMER_SUPPORT_EVENTS } from '@common/analytics/AnalyticsEvents';
import MenuItem from './menu_item';
import MyOrdersBadge from '../header/my_orders_badge';
import MessageCountBadge from '../header/message_count_badge';
import SpecialitiesMenuItem from './specialities_menu_item';
import classes from './classes.module.scss';
import { appLinks } from '../../config/const';
import HeaderLogo from '../header/header_logo';
import { getCurrentLocalization, DEFAULT_LANGUAGE } from '../../config/i18n';
import { appRoutes } from '../../config/app_routes';
import { isKA, isPGW } from '../../../common/config/utils';
import ChangeLanguage from '../change_language';
import { isActiveRoute } from '../routes/routes_helpers';
import Badge from './badge';
import { formatLocalCurrency, isMulticurrencyAllowed } from '../../config/util';
import { renderSideMenuIcon } from './render_side_menu_icon';

const sideMenuIconNames = {
  home: 'home',
  promo: 'promo',
  blog: 'blog',
  language: 'language',
  cashback: 'cashback',
  support: 'support',
  forAdvisors: 'for_advisors',
  settings: 'settings',
  myOrders: 'my_orders',
  favAdvisors: 'fav_advisors',
  credit: 'credit',
  paymentSources: 'payment_sources',
  sideMenuClosed: 'side_menu_closed',
  horoscope: 'horoscope',
  spiritualHub: 'spiritual_hub_menu_icon',
  advisorVerify: 'advisor_verify',
  satisfactionGuaranteed: 'satisfaction_guaranteed_menu_icon'
};

const language = getCurrentLocalization();

export const renderChangeLanguageMenuItem = ({
  t, showChangeLanguageOrCurrency, toggleChangeLanguageOrCurrencyPopup, currencyCode, toggleDrawer
}) => {
  if (isKA()) return null;
  return (
    <>
      <MenuItem
        title={ { text: isMulticurrencyAllowed() ? t('sideMenu.languageAndCurrency') : t('sideMenu.language') } }
        icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.language, path: '/language' }) }
        onClick={ () => toggleChangeLanguageOrCurrencyPopup({ show: true }) }
        extraNode={ <div className={ classes.languageSign }>{ isMulticurrencyAllowed() && currencyCode ? `${ language } | ${ currencyCode }` : language }</div> }
        rightPos
      />
      {createPortal(
        <ChangeLanguage
          show={ showChangeLanguageOrCurrency }
          onClose={ toggleChangeLanguageOrCurrencyPopup }
          onCurrencyChanged={ toggleDrawer }
        />,
        document.body
      )}
    </>
  );
};

const renderBlogMenuItem = (t) => (isPGW() ? (
  <MenuItem
    title={ { text: t('sideMenu.blog') } }
    icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.blog, path: appLinks.blog }) }
    asHref
    linkTo={ language === DEFAULT_LANGUAGE ? appLinks.blog : `${ appLinks.blog }/${ language }` }
    openInNewTab
  />
) : (
  <MenuItem
    title={ { text: t('sideMenu.articles') } }
    icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.blog, path: appLinks.articles }) }
    asHref
    linkTo={ appLinks.articles }
    openInNewTab
  />
));

const renderMyHoroscopeMenuItem = (t, userLoggedIn) => (
  <>
    {!userLoggedIn ? <div className={ classes.sideMenuItem } data-testid="horoscope-divider"><div className={ classes.menuDividers } /></div> : null}
    <MenuItem
      icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.horoscope, path: appLinks.myHoroscope, needChekActive: false }) }
      title={ { text: t('sideMenu.myHoroscope') } }
      linkTo={ appLinks.myHoroscope }
      asHref
    />
  </>
);

function SideMenu({
  t, isSufficientForCashback = false, cashbackAmount = '0.0', shouldShowPayoutMethodMenuItem, showSpiritualHubMenuItem = false,
  showNewSpiritualHubBadge = false, toggleDrawer,  visible, satisfactionGuarantee, showAddFundsMenuItem = false, currencyCode
}) {
  const userLoggedIn = window.currentUserId;
  const [showChangeLanguageOrCurrency, setShowChangeLanguageOrCurrency] = useState(false);

  const toggleChangeLanguageOrCurrencyPopup = ({ show }) => {
    setShowChangeLanguageOrCurrency(show);
  };

  const handleCustomerSupportClick = () => {
    analyticsService.trackEvent(CUSTOMER_SUPPORT_EVENTS.SUPPORT_CLICKED, { 'click source': 'menu' });
    window.open(appLinks.customerSupport, '_blank', 'noopener,noreferrer');
  };

  const cashbackItem = () => {
    if (!isPGW()) return null;
    return (
      <MenuItem
        title={ { text: t('sideMenu.cashback'), active: isActiveRoute(appRoutes.cashback) } }
        icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.cashback, path: appRoutes.cashback }) }
        linkTo={ appRoutes.cashback }
        extraNode={ isSufficientForCashback ? (
          <div
            data-testid="cashback-amount"
            className={
      isSufficientForCashback ? classes.cashbackActive : classes.cashbackInactive
      }
          >
            { `${ formatLocalCurrency(cashbackAmount) }` }
          </div>
        ) : null }
      />
    );
  };

  function renderLoggedOutSideMenu() {
    return (
      <div className={ classes.sideMenu }>
        <MenuItem
          title={ { text: t('sideMenu.home'), active: isActiveRoute(appRoutes.home) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.home, path: appRoutes.home }) }
          linkTo={ appRoutes.home }
        />
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        <SpecialitiesMenuItem />
        {renderMyHoroscopeMenuItem(t, userLoggedIn)}
        {showSpiritualHubMenuItem && (
        <MenuItem
          title={ { text: t('sideMenu.gamified_hub'), active: isActiveRoute(appRoutes.gamifiedHub) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.spiritualHub, path: appRoutes.gamifiedHub }) }
          linkTo={ appRoutes.gamifiedHub }
          extraNode={ <Badge show={ showNewSpiritualHubBadge } /> }
        />
        )}
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        <MenuItem
          title={ { text: t('sideMenu.apply_promo_code'), active: isActiveRoute(appRoutes.promoCode) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.promo, path: appRoutes.promoCode }) }
          linkTo={ appRoutes.promoCode }
        />
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        {renderBlogMenuItem(t)}
        { renderChangeLanguageMenuItem({
          t, showChangeLanguageOrCurrency, toggleChangeLanguageOrCurrencyPopup, currencyCode, toggleDrawer
        }) }
        <MenuItem
          title={ { text: t('sideMenu.customer_support') } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.support, path: appLinks.customerSupport }) }
          linkTo={ appLinks.customerSupport }
          onClick={ handleCustomerSupportClick }
        />
        {satisfactionGuarantee && (
          <MenuItem
            title={ { text: satisfactionGuarantee.screenTitle } }
            icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.satisfactionGuaranteed, path: satisfactionGuarantee.link }) }
            linkTo={ satisfactionGuarantee.link }
            asHref
          />
        )}
        <MenuItem
          title={ { text: t('sideMenu.advisor_verify') } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.advisorVerify, path: appLinks.advisorVerify }) }
          linkTo={ appLinks.advisorVerify }
          asHref
        />
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        <MenuItem
          title={ { text: t('sideMenu.menu_apply_advisor') } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.forAdvisors, path: appLinks.forAdvisors }) }
          linkTo={ appLinks.forAdvisors }
          asHref
          openInNewTab
        />
      </div>
    );
  }

  function renderLoggedInSideMenu() {
    return (
      <div className={ classes.sideMenu }>
        <MenuItem
          title={ { text: t('sideMenu.home'), active: isActiveRoute(appRoutes.home) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.home, path: appRoutes.home }) }
          linkTo={ appRoutes.home }
        />
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        <SpecialitiesMenuItem />
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        {renderMyHoroscopeMenuItem(t, userLoggedIn)}
        {showSpiritualHubMenuItem && (
        <MenuItem
          title={ { text: t('sideMenu.gamified_hub'), active: isActiveRoute(appRoutes.gamifiedHub) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.spiritualHub, path: appRoutes.gamifiedHub }) }
          linkTo={ appRoutes.gamifiedHub }
          extraNode={ <Badge show={ showNewSpiritualHubBadge } /> }
        />
        )}
        <MenuItem
          title={ { text: t('activity.my_activity'), active: isActiveRoute(appRoutes.myOrders) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.myOrders, path: appRoutes.myOrders }) }
          linkTo={ appRoutes.myOrders }
          extraNode={ (
            <div className={ classes.badgeContainer }>
              <MyOrdersBadge />
              <MessageCountBadge />
            </div>
      ) }
        />
        <MenuItem
          title={ { text: t('sideMenu.favorite_advisors'), active: isActiveRoute(appRoutes.favoriteAdvisors) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.favAdvisors, path: appRoutes.favoriteAdvisors }) }
          linkTo={ { pathname: appRoutes.favoriteAdvisors, state: { needRefresh: true } } }
        />
        {showAddFundsMenuItem && (
          <MenuItem
            title={ { text: t('sideMenu.add_funds'), active: isActiveRoute(appRoutes.addFunds) } }
            icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.credit, path: appRoutes.addFunds }) }
            linkTo={ appRoutes.addFunds }
          />
        )}
        { shouldShowPayoutMethodMenuItem ? (
          <MenuItem
            title={ { text:t('sideMenu.payment_methods'), active: isActiveRoute(appRoutes.paymentSources) } }
            icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.paymentSources, path: appRoutes.paymentSources }) }
            linkTo={ appRoutes.paymentSources }
          />
        ) : null}
        {cashbackItem()}
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        <MenuItem
          title={ { text: t('sideMenu.apply_promo_code'), active: isActiveRoute(appRoutes.promoCode) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.promo, path: appRoutes.promoCode }) }
          linkTo={ appRoutes.promoCode }
        />
        <div className={ classes.sideMenuItem }><div className={ classes.menuDividers } /></div>
        {renderBlogMenuItem(t)}
        { renderChangeLanguageMenuItem({
          t, showChangeLanguageOrCurrency, toggleChangeLanguageOrCurrencyPopup, currencyCode, toggleDrawer
        }) }
        <MenuItem
          title={ { text: t('sideMenu.customer_support') } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.support, path: appLinks.customerSupport }) }
          onClick={ handleCustomerSupportClick }
          linkTo={ appLinks.customerSupport }
        />
        {satisfactionGuarantee && (
          <MenuItem
            title={ { text: satisfactionGuarantee.screenTitle } }
            icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.satisfactionGuaranteed, path: satisfactionGuarantee.link }) }
            linkTo={ satisfactionGuarantee.link }
            asHref
          />
        )}
        <MenuItem
          title={ { text: t('sideMenu.advisor_verify') } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.advisorVerify, path: appLinks.advisorVerify }) }
          linkTo={ appLinks.advisorVerify }
          asHref
        />
        <MenuItem
          title={ { text: t('sideMenu.menu_apply_advisor') } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.forAdvisors, path: appLinks.forAdvisors }) }
          linkTo={ appLinks.forAdvisors }
          asHref
          openInNewTab
        />
        <MenuItem
          title={ { text: t('sideMenu.settings'), active: isActiveRoute(appRoutes.settings) } }
          icon={ renderSideMenuIcon({ iconName: sideMenuIconNames.settings, path: appRoutes.settings }) }
          linkTo={ appRoutes.settings }
        />
      </div>
    );
  }

  const renderContent = () => (
    <div data-testid="SideMenu">
      <div suppressHydrationWarning className={ classes.drawer } style={ visible ? { '--display': 'flex' } : { '--display': 'none' } }>
        <div className={ classes.sideMenuHeaderLogoContainer } suppressHydrationWarning>
          <HeaderLogo toggleDrawer={ toggleDrawer } sideMenuIcon={ sideMenuIconNames.sideMenuClosed } onSideMenu />
        </div>
        { userLoggedIn ? renderLoggedInSideMenu() : renderLoggedOutSideMenu() }
      </div>
      <div suppressHydrationWarning className={ classes.drawerMask } onClick={ toggleDrawer } onKeyDown={ toggleDrawer } style={ visible ? { '--display': 'flex' } : { '--display': 'none' } } />
    </div>
  );

  return renderContent();
}

export default SideMenu;
